import { motion } from "framer-motion";
import {
  Stethoscope, FileText, Building2, ClipboardCheck, ShieldCheck, Truck, PackageCheck, ArrowUpRight,
} from "lucide-react";
import { APP_URL } from "@/lib/urls";

const NODES = [
  { title: "Doctor", note: "creates Rx", icon: Stethoscope },
  { title: "Rx", note: "is digital and structured", icon: FileText },
  { title: "Lab", note: "receives and confirms", icon: Building2 },
  { title: "Production", note: "tasks assigned", icon: ClipboardCheck },
  { title: "Quality", note: "signed off with photos", icon: ShieldCheck },
  { title: "Dispatch", note: "routed to driver", icon: Truck },
  { title: "Delivery", note: "signed and closed", icon: PackageCheck },
];

export default function WorkflowTimeline() {
  return (
    <section
      data-testid="workflow-timeline"
      className="relative py-16 sm:py-20"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
              Workflow
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
              One clear pipeline for every case
            </h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            No spreadsheets, sticky notes, or missed handoffs. Every stage is timestamped and easy to search.
          </p>
        </div>

        {/* Desktop: horizontal */}
        <div className="hidden lg:block relative">
          <div className="relative">
            <svg className="absolute inset-x-0 top-12 h-8 w-full" viewBox="0 0 1200 40" preserveAspectRatio="none">
              <defs>
                <linearGradient id="wf-g" x1="0" x2="1">
                  <stop offset="0%" stopColor="#2AA6DE" />
                  <stop offset="55%" stopColor="#82298D" />
                  <stop offset="100%" stopColor="#C9539F" />
                </linearGradient>
              </defs>
              <motion.line
                x1="60"
                x2="1140"
                y1="20"
                y2="20"
                stroke="url(#wf-g)"
                strokeWidth="2"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0.2 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
              />
            </svg>
            <div className="grid grid-cols-7 gap-4 relative">
              {NODES.map((n, i) => {
                const Icon = n.icon;
                return (
                  <motion.div
                    key={n.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    className="flex flex-col items-center text-center"
                    data-testid={`workflow-node-${i}`}
                  >
                    <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                      {i + 1}
                    </div>
                    <div className="mt-2 h-14 w-14 rounded-2xl glass hairline glow-brand inline-flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 bg-brand-gradient opacity-0 hover:opacity-100 transition-opacity" />
                      <Icon className="h-5 w-5 relative text-foreground" />
                    </div>
                    <div className="mt-4 font-heading font-semibold text-sm tracking-tight">
                      {n.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{n.note}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile: vertical */}
        <div className="lg:hidden relative">
          <div className="absolute left-6 top-2 bottom-2 w-px bg-gradient-to-b from-brand-cyan via-brand-plum to-brand-rose opacity-50" />
          <div className="space-y-6">
            {NODES.map((n, i) => {
              const Icon = n.icon;
              return (
                <motion.div
                  key={n.title}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="pl-16 relative"
                >
                  <div className="absolute left-0 top-0 h-12 w-12 rounded-2xl glass hairline inline-flex items-center justify-center">
                    <Icon className="h-4 w-4 text-brand-plum" />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                    {i + 1}
                  </div>
                  <div className="font-heading text-lg font-semibold">{n.title}</div>
                  <div className="text-sm text-muted-foreground">{n.note}</div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href={APP_URL}
            data-testid="workflow-cta-modernize"
            className="group inline-flex items-center gap-2 pl-6 pr-4 py-3.5 rounded-full bg-foreground text-background font-semibold text-sm hover:opacity-90 transition-opacity"
          >
            Modernize your workflow
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-gradient text-white transition-transform group-hover:translate-x-0.5">
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
